<script setup>
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import { getRequest } from "@/utils/request.js";
import MessageModal from "@/views/chat/modules/MessageModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const cityOptions = ref([]);
const selectedDistrict = ref([]);

const columns = [
  {
    key: "userRealName",
    dataIndex: "userRealName",
    title: "聊天对象",
    width: "10%",
  },
  {
    key: "userType",
    dataIndex: "userType",
    title: "聊天对象角色",
    width: "10%",
  },
  {
    key: "userAvatar",
    dataIndex: "userAvatar",
    title: "头像",
    width: "10%",
  },
  {
    key: "targetRealName",
    dataIndex: "targetRealName",
    title: "聊天对象",
    width: "10%",
  },
  {
    key: "targetUserType",
    dataIndex: "targetUserType",
    title: "聊天对象角色",
    width: "10%",
  },
  {
    key: "targetAvatar",
    dataIndex: "targetAvatar",
    title: "头像",
    width: "10%",
  },
  {
    key: "jobTitle",
    dataIndex: "jobTitle",
    title: "职位标题",
    width: "10%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadCity = () => {
  getRequest("/city").then((res) => {
    if (res.success) {
      const cityData = res.data.map((province) => {
        const children = province.cityList.map((city) => {
          const { id, name } = city;
          return { id, name, children: city.districtList };
        });

        const { id, name } = province;
        return { id, name, children };
      });
      console.info("cityData", cityData);
      cityOptions.value = cityData;
    }
  });
};

const loadData = () => {
  loading.value = true;

  const [provinceId, cityId, districtId] = unref(selectedDistrict);

  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign(
    {},
    unref(searchFormModel),
    {
      pageNo: current,
      pageSize,
    },
    { provinceId, cityId, districtIds: [districtId] },
  );

  getRequest("/chat", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

const resetForm = () => {
  formRef.value.resetFields();
  selectedDistrict.value = [];
  loadData();
};

const handlePreview = (record) => {
  modalRef.value.title = "聊天记录";
  modalRef.value.handleShow(record);
};

onMounted(() => {
  loadCity();
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="姓名" name="realName">
              <a-input
                v-model:value.trim="searchFormModel.realName"
                placeholder="请输入姓名"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="性别" name="gender">
              <a-select
                v-model:value.trim="searchFormModel.gender"
                placeholder="请选择性别"
              >
                <a-select-option value="MALE"> 男</a-select-option>
                <a-select-option value="FEMALE"> 女</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="求职状态" name="jobSeekerStatus">
              <a-select
                v-model:value="searchFormModel.jobSeekerStatus"
                placeholder="请选择求职状态"
              >
                <a-select-option value="1">离职-随时到岗</a-select-option>
                <a-select-option value="2">在职-月内到岗</a-select-option>
                <a-select-option value="3">在职-考虑机会</a-select-option>
                <a-select-option value="4">在职-暂不考虑</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="地区" name="district">
              <a-cascader
                v-model:value="selectedDistrict"
                :options="cityOptions"
                :fieldNames="{
                  label: 'name',
                  value: 'id',
                }"
                change-on-select
                placeholder="请选择地区"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template
            v-if="
              column.dataIndex === 'userRealName' ||
              column.dataIndex === 'targetRealName'
            "
          >
            {{ record[column.dataIndex] || "未完善信息用户" }}
          </template>

          <template
            v-if="
              column.dataIndex === 'userAvatar' ||
              column.dataIndex === 'targetAvatar'
            "
          >
            <a-avatar :size="80" :src="$imageUrl(record[column.dataIndex])" />
          </template>

          <template
            v-if="
              column.dataIndex === 'userType' ||
              column.dataIndex === 'targetUserType'
            "
          >
            <template v-if="record[column.dataIndex] === 'RECRUITER'">
              招聘者
            </template>
            <template v-else-if="record[column.dataIndex] === 'JOB_SEEKER'">
              求职者
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a type="link" @click="handlePreview(record)">查看记录</a>
          </template>
        </template>
      </a-table>
    </a-card>
    <message-modal ref="modalRef" />
  </div>
</template>

<style scoped lang="scss"></style>
